
import { PropType, defineComponent } from "vue";
import CurrencyInputCustom from "@/components/forms-elements/CurrencyInputCustom.vue";
import QuantityInputCustom from "@/components/forms-elements/QuantityInputCustom.vue";
import {
  VAT_TU_TABLE_COLUMN_WIDTHS,
  VatTuTable,
} from "@/components/shared/vat-tu-table";
import {
  calcLineAmount,
  formatAmount,
  formatQuantity,
  formatUnitPrice,
} from "@/core/helpers/number-format";
import {
  swalNotification,
  transformFirstLetterToUpperCase,
} from "@/core/helpers/utils";
import { IVatTuBienBan } from "@/core/interfaces/ApiResponses";
import ToolService from "@/core/services/Tool.service";

type VatTuBienBanFooterRow = {
  _footerType: "total" | "text";
  _footerLabel: string;
};

type VatTuBienBanTableRow = IVatTuBienBan | VatTuBienBanFooterRow;

function isFooterRow(row: VatTuBienBanTableRow): row is VatTuBienBanFooterRow {
  return "_footerType" in row;
}

export default defineComponent({
  name: "vat-tu-bien-ban",

  components: {
    CurrencyInputCustom,
    QuantityInputCustom,
    VatTuTable,
  },

  emits: ["on-change", "on-change-phuong-thuc", "change-don-gia"],

  props: {
    isEditable: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
    allowEditingDonGia: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    forPhieuNhapLe: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    vatTuItems: {
      type: Array as PropType<Array<IVatTuBienBan>>,
      default: () => [],
    },
  },

  data() {
    return {
      columnWidths: VAT_TU_TABLE_COLUMN_WIDTHS,
      items: [] as IVatTuBienBan[],
      soLuongVatTuBanDau: [] as number[],
      totalPriceInText: "Không",
    };
  },

  computed: {
    emptyText() {
      return "Chưa có vật tư trong phiếu nhập.";
    },
    labelColspan() {
      return this.footerColumnIds.indexOf("thanh_tien");
    },
    thanhTienColumnIndex() {
      return this.footerColumnIds.indexOf("thanh_tien");
    },
    trailingColspan() {
      return this.footerColumnIds.length - this.thanhTienColumnIndex;
    },
    footerColumnIds(): string[] {
      const columnIds = ["stt", "ten_vat_tu", "ma_vat_tu", "don_vi_tinh"];

      if (!this.forPhieuNhapLe) {
        columnIds.push("so_luong_can");
      }

      columnIds.push(
        "so_luong_nhap",
        "don_gia",
        "thanh_tien",
        "kq_dat",
        "kq_khong_dat",
        "phuong_thuc"
      );

      return columnIds;
    },
    totalPriceInNumber() {
      return Math.round(
        this.items.reduce(
          (acc, vatTuItem) =>
            +vatTuItem.so_luong_trong_bien_ban * +vatTuItem.pivot.so_tien + acc,
          0
        )
      );
    },
    displayItems(): VatTuBienBanTableRow[] {
      if (!this.items.length) {
        return [];
      }

      return [
        ...this.items,
        {
          _footerType: "total",
          _footerLabel: "Tổng cộng:",
        },
        {
          _footerType: "text",
          _footerLabel: "Tổng số tiền (viết bằng chữ):",
        },
      ];
    },
  },

  watch: {
    vatTuItems: {
      handler(newItems: IVatTuBienBan[]) {
        this.items = newItems.map((item) => ({
          ...item,
          pivot: { ...item.pivot },
        }));
        this.soLuongVatTuBanDau = this.items.map(
          (item) => +item.so_luong_trong_bien_ban
        );
      },
      immediate: true,
    },
    totalPriceInNumber: {
      async handler(value) {
        if (!value) {
          this.totalPriceInText = "Không";

          return;
        }

        const {
          data: { data },
        } = await ToolService.convertPriceToText(value);

        this.totalPriceInText = transformFirstLetterToUpperCase(data);
      },
      immediate: true,
    },
  },

  methods: {
    calcLineAmount,
    formatAmount,
    formatQuantity,
    formatUnitPrice,

    isFooterRow,

    getItemIndex(row: VatTuBienBanTableRow) {
      if (isFooterRow(row)) {
        return -1;
      }

      return this.items.findIndex((item) => item.id === row.id);
    },

    footerRowClassName({ row }: { row: VatTuBienBanTableRow }) {
      return isFooterRow(row) ? "vat-tu-table__footer-row" : "";
    },

    footerSpanMethod({
      row,
      column,
      columnIndex,
    }: {
      row: VatTuBienBanTableRow;
      column: { property?: string };
      columnIndex: number;
    }) {
      if (!isFooterRow(row)) {
        return;
      }

      const {
        footerColumnIds,
        labelColspan,
        thanhTienColumnIndex,
        trailingColspan,
      } = this;
      const columnId = column.property;

      if (columnId) {
        const columnPosition = footerColumnIds.indexOf(columnId);

        if (columnPosition < 0) {
          return { rowspan: 0, colspan: 0 };
        }

        if (columnPosition === 0) {
          return { rowspan: 1, colspan: labelColspan };
        }

        if (columnPosition > 0 && columnPosition < thanhTienColumnIndex) {
          return { rowspan: 0, colspan: 0 };
        }

        if (columnId === "thanh_tien") {
          if (row._footerType === "text") {
            return { rowspan: 1, colspan: trailingColspan };
          }

          return { rowspan: 1, colspan: 1 };
        }

        if (row._footerType === "total" && columnId === "kq_dat") {
          return { rowspan: 1, colspan: trailingColspan - 1 };
        }

        return { rowspan: 0, colspan: 0 };
      }

      if (columnIndex === 0) {
        return { rowspan: 1, colspan: labelColspan };
      }

      if (columnIndex > 0 && columnIndex < thanhTienColumnIndex) {
        return { rowspan: 0, colspan: 0 };
      }

      if (columnIndex === thanhTienColumnIndex) {
        if (row._footerType === "text") {
          return { rowspan: 1, colspan: trailingColspan };
        }

        return { rowspan: 1, colspan: 1 };
      }

      if (
        row._footerType === "total" &&
        columnIndex === thanhTienColumnIndex + 1
      ) {
        return { rowspan: 1, colspan: trailingColspan - 1 };
      }

      return { rowspan: 0, colspan: 0 };
    },

    async onChangedSoLuongHandler(
      vatTu: IVatTuBienBan,
      index: number,
      _value: number
    ) {
      if (index < 0) {
        return;
      }

      const previousValue = this.soLuongVatTuBanDau[index];

      if (+_value < 0) {
        await swalNotification("Phải lớn hơn hoặc bằng 0", "error");

        this.items[index].so_luong_trong_bien_ban = previousValue;

        this.$emit("on-change", { items: this.items, id_vat_tu: vatTu.id });

        return;
      }

      if (+this.items[index].so_luong_can < +_value && !this.forPhieuNhapLe) {
        await swalNotification("Vượt quá số lượng vật tư cần", "error");

        this.items[index].so_luong_trong_bien_ban =
          +this.items[index].so_luong_can;

        this.$emit("on-change", { items: this.items, id_vat_tu: vatTu.id });

        return;
      }

      this.$emit("on-change", { items: this.items, id_vat_tu: vatTu.id });
    },

    onChangedPhuongThucHandler(index: number, value: string) {
      if (index < 0) {
        return;
      }

      this.$emit("on-change-phuong-thuc", {
        index,
        value,
      });
    },

    handleDonGiaUpdate({ value, index }: { value: number; index: number }) {
      if (index < 0) {
        return;
      }

      this.$emit("change-don-gia", {
        value,
        index,
      });
    },
  },
});
